import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImgComparisonSlider } from '@img-comparison-slider/react';
import { useAtom, useAtomValue } from 'jotai';
import { Settings } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useElementResize } from '@/hooks/use-element-resize';
import useResizeObserver from '@/hooks/use-resize-observer';
import { cn } from '@/lib/utils';

import { selectedPreviewImageAtom, uploadedImagesAtom } from '../atoms/imageFiles';
import { ImageRenderingCarousel } from './image-carousel';
import { ImageRenderingToolbar } from './toolbar';

interface ImageEditorProps {
  showMobileSettings: boolean;
  setShowMobileSettings: Dispatch<SetStateAction<boolean>>;
  onDownload: () => void;
  onExport: () => void;
}

const CAROUSEL_ITEM_WIDTH = 180;
const MOBILE_CAROUSEL_ITEM_WIDTH = 120;

export const ImageEditor = ({
  showMobileSettings,
  setShowMobileSettings,
  onDownload,
  onExport,
}: ImageEditorProps) => {
  const { t } = useTranslation();
  const { ref, size } = useElementResize();
  const isMobileView = !useResizeObserver(640);

  const [selectedPreviewImage, setSelectedPreviewImage] = useAtom(selectedPreviewImageAtom);
  const uploadedImages = useAtomValue(uploadedImagesAtom);

  const showArrowButtons = useMemo(() => {
    if (ref?.current === null) return false;
    return (
      (size.width || (ref.current as HTMLDivElement)?.offsetWidth) <
      uploadedImages?.length * (CAROUSEL_ITEM_WIDTH + 16)
    );
  }, [size, uploadedImages, ref]);

  useEffect(() => {
    if (uploadedImages?.length) {
      setSelectedPreviewImage(uploadedImages[uploadedImages?.length - 1]);
    }
  }, [uploadedImages, setSelectedPreviewImage]);

  return (
    <div
      className={cn('relative w-full flex-col', !showMobileSettings ? 'flex' : 'hidden sm:flex')}
    >
      <div
        className={cn(
          'relative mx-auto flex min-h-[160px] flex-col justify-center overflow-hidden rounded-sm shadow-lg sm:min-h-[300px]',
          !selectedPreviewImage?.originalImage && 'w-full bg-white'
        )}
      >
        {selectedPreviewImage ? (
          <div className="absolute right-2 top-2 z-10">
            <ImageRenderingToolbar onDownload={onDownload} onExport={onExport} />
          </div>
        ) : null}
        {!showMobileSettings && uploadedImages.length !== 0 ? (
          <Button
            className="absolute left-2 top-2 z-[100] flex h-12 w-12 items-center justify-center rounded-sm border-[1px] border-primary-light bg-text-primary px-1 hover:!bg-[#2C414B] sm:hidden"
            onClick={() => setShowMobileSettings(true)}
          >
            <Settings className="text-primary-light" />
          </Button>
        ) : null}
        <div className="flex items-center justify-center">
          {selectedPreviewImage ? (
            selectedPreviewImage?.renderedImage ? (
              <ImgComparisonSlider>
                <img
                  slot="first"
                  src={selectedPreviewImage.originalImage}
                  className="h-full w-full bg-white"
                  height="100%"
                  width="100%"
                />
                <img
                  slot="second"
                  src={selectedPreviewImage.renderedImage}
                  className="bg-grey h-full w-full"
                  height="100%"
                  width="100%"
                />
              </ImgComparisonSlider>
            ) : (
              <img
                className="object-contain"
                src={selectedPreviewImage.originalImage}
                style={{ maxHeight: isMobileView ? 'calc(100vh - 180px)' : 'calc(100vh - 420px)' }}
              />
            )
          ) : (
            <span className="text-center text-xl font-medium text-[#B2B2B2]">
              {t('noImageSelected')}
            </span>
          )}
        </div>
      </div>
      {selectedPreviewImage ? (
        <div className="mt-2 h-16 sm:mt-5 sm:h-[110px]">
          <ImageRenderingCarousel
            className="absolute gap-2"
            imageWrapperClassName={(active: boolean) =>
              cn(
                'max-w-52 overflow-hidden rounded-sm border border-solid',
                active ? 'border-primary-light' : 'border-text-primary'
              )
            }
            imageWidth={isMobileView ? MOBILE_CAROUSEL_ITEM_WIDTH : CAROUSEL_ITEM_WIDTH}
            showArrowButtons={showArrowButtons}
          />
        </div>
      ) : null}
    </div>
  );
};
